import type { CommandBody, GameProjection } from '@lafayette/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';

const DRAW_DELAY_MS = 5_000;
const END_TURN_DELAY_MS = 10_000;
const TICK_MS = 100;

export interface TurnAutomation {
  drawProgress: number;
  endTurnProgress: number;
  endTurnSeconds: number;
  showEndTurnNotice: boolean;
  endTurnPaused: boolean;
  pauseEndTurn: () => void;
  drawForTurn: () => void;
  endTurn: () => void;
}

export function useTurnAutomation(
  projection: GameProjection,
  send: (body: CommandBody) => void,
  connected: boolean,
): TurnAutomation {
  const [drawProgress, setDrawProgress] = useState(0);
  const [endTurnProgress, setEndTurnProgress] = useState(0);
  const [endTurnSeconds, setEndTurnSeconds] = useState(END_TURN_DELAY_MS / 1_000);
  const [endTurnPaused, setEndTurnPaused] = useState(false);
  const drawPending = useRef(false);
  const endTurnPending = useRef(false);

  const isOwnTurn = projection.turn.playerId === projection.self.id;
  const canDraw =
    connected &&
    isOwnTurn &&
    projection.stage === 'awaitingDraw' &&
    projection.legalCommands.some((command) => command.type === 'drawForTurn');
  const canEndTurn =
    connected &&
    isOwnTurn &&
    projection.stage === 'main' &&
    projection.turn.actionsUsed >= projection.turn.actionsPerTurn &&
    projection.legalCommands.some((command) => command.type === 'endTurn');

  useEffect(() => {
    if (!canDraw) {
      drawPending.current = false;
      setDrawProgress(0);
      return;
    }

    const startedAt = performance.now();
    let timer = 0;
    const update = (): void => {
      const elapsed = performance.now() - startedAt;
      const progress = Math.min(elapsed / DRAW_DELAY_MS, 1);
      setDrawProgress(progress);
      if (progress === 1) {
        window.clearInterval(timer);
        if (!drawPending.current) {
          drawPending.current = true;
          send({ type: 'drawForTurn' });
        }
      }
    };

    update();
    timer = window.setInterval(update, TICK_MS);
    return () => window.clearInterval(timer);
  }, [canDraw, send]);

  useEffect(() => {
    if (!canEndTurn) {
      endTurnPending.current = false;
      setEndTurnProgress(0);
      setEndTurnSeconds(END_TURN_DELAY_MS / 1_000);
      setEndTurnPaused(false);
      return;
    }
    if (endTurnPaused) return;

    const startedAt = performance.now();
    let timer = 0;
    const update = (): void => {
      const elapsed = performance.now() - startedAt;
      const remaining = Math.max(END_TURN_DELAY_MS - elapsed, 0);
      const progress = Math.min(elapsed / END_TURN_DELAY_MS, 1);
      setEndTurnProgress(progress);
      setEndTurnSeconds(Math.max(1, Math.ceil(remaining / 1_000)));
      if (progress === 1) {
        window.clearInterval(timer);
        if (!endTurnPending.current) {
          endTurnPending.current = true;
          send({ type: 'endTurn' });
        }
      }
    };

    update();
    timer = window.setInterval(update, TICK_MS);
    return () => window.clearInterval(timer);
  }, [canEndTurn, endTurnPaused, projection.turn.playerId, send]);

  const pauseEndTurn = useCallback(() => setEndTurnPaused(true), []);
  const drawForTurn = useCallback(() => {
    drawPending.current = true;
    send({ type: 'drawForTurn' });
  }, [send]);
  const endTurn = useCallback(() => {
    endTurnPending.current = true;
    send({ type: 'endTurn' });
  }, [send]);

  return {
    drawProgress,
    endTurnProgress,
    endTurnSeconds,
    showEndTurnNotice: canEndTurn,
    endTurnPaused,
    pauseEndTurn,
    drawForTurn,
    endTurn,
  };
}
